import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Lightning,
  Shield,
  ChartLine,
  InstagramLogo,
  FacebookLogo,
  WhatsappLogo,
  Star,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { WarrantySection } from '../components/WarrantySection';

export const LandingPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1778319300615-03d67b057e1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjBibHVlfGVufDB8fHx8MTc4Mjc3MzY2OHww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00E676]/40 bg-[#00E676]/10 backdrop-blur-sm mb-6 animate-pulse-glow">
              <Lightning size={16} weight="fill" className="text-[#00E676]" />
              <span className="text-xs font-bold tracking-widest text-[#00E676] uppercase">
                🔥 Ofertas activas · Hasta 33% OFF por tiempo limitado
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white"
              data-testid="hero-title"
            >
              Hacé crecer tus redes
              <br />
              <span className="text-gradient-brand">sin límites.</span>
            </h1>
            <p
              className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl mx-auto"
              data-testid="hero-description"
            >
              Seguidores, likes y vistas reales para Instagram, TikTok y Facebook.
              Entrega automática · pago seguro · resultados garantizados.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/checkout" data-testid="hero-cta-button">
                <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6">
                  Comprar Ahora
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
              <Link to="/checkout" data-testid="hero-services-link">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto text-lg px-8 py-6 bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
                >
                  Ver Servicios
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Discount Highlight Band */}
      <div className="relative overflow-hidden border-y border-[#00E676]/30 bg-gradient-to-r from-[#00E676]/10 via-white/5 to-[#0066FF]/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-3 flex-wrap text-sm sm:text-base">
          <div className="flex items-center gap-1">
            <span className="text-2xl">🔥</span>
            <Star size={16} weight="fill" className="text-[#F6B40E]" />
          </div>
          <span className="font-semibold text-foreground">
            Descuentos activos en todos los servicios · Ahorrá hasta un
          </span>
          <code className="px-2 py-0.5 rounded-md bg-[#00E676]/10 border border-[#00E676]/40 text-[#00E676] font-bold tracking-wider text-sm">
            33% OFF
          </code>
        </div>
      </div>

      {/* How It Works */}
      <section className="py-20 bg-muted/30" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
              <Lightning size={16} weight="fill" />
              PRECIOS REBAJADOS
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4">
              Hasta <span className="text-primary">33% OFF</span> por tiempo limitado
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Ahorrá en todos los servicios · entrega instantánea · sin necesidad de cuenta.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Elegí servicio',
                description: 'Seleccioná la plataforma y el servicio',
              },
              {
                step: '2',
                title: 'Ingresá el enlace',
                description: 'Pegá la URL de tu perfil o publicación',
              },
              {
                step: '3',
                title: 'Confirmá',
                description: 'Definí la cantidad y completá el pago',
              },
              {
                step: '4',
                title: 'Ahorrá 33%',
                description: 'Precios rebajados en todos los paquetes',
              },
            ].map((item, index) => (
              <Card
                key={index}
                className="p-6 text-center hover:-translate-y-1 transition-all duration-200 border-border"
                data-testid={`how-it-works-step-${index + 1}`}
              >
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-medium mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4">
              ¿Por qué elegirnos?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              La mejor calidad y servicio del mercado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Lightning size={32} weight="duotone" />,
                title: 'Entrega instantánea',
                description: 'Tus pedidos se procesan automáticamente en segundos',
              },
              {
                icon: <Shield size={32} weight="duotone" />,
                title: '100% Seguro',
                description: 'Protección total de tus datos y cuentas',
              },
              {
                icon: <ChartLine size={32} weight="duotone" />,
                title: 'Resultados reales',
                description: 'Seguidores y engagement de alta calidad',
              },
            ].map((benefit, index) => (
              <Card
                key={index}
                className="p-8 hover:-translate-y-1 transition-all duration-200 border-border"
                data-testid={`benefit-card-${index + 1}`}
              >
                <div className="text-primary mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-medium mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-muted/30" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4">
              Nuestros servicios
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Servicios para las plataformas más populares
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                platform: 'Instagram',
                services: ['Seguidores', 'Likes'],
                color: 'from-pink-500 via-purple-500 to-orange-400',
              },
              {
                platform: 'TikTok',
                services: ['Likes', 'Visualizaciones'],
                color: 'from-cyan-400 to-pink-500',
              },
              {
                platform: 'Facebook',
                services: ['Seguidores', 'Likes'],
                color: 'from-blue-600 to-blue-400',
              },
            ].map((platform, index) => (
              <Card
                key={index}
                className="p-8 border-border hover:-translate-y-1 transition-all duration-200"
                data-testid={`platform-card-${platform.platform.toLowerCase()}`}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${platform.color} rounded-xl mb-4`}></div>
                <h3 className="text-2xl font-semibold mb-4">{platform.platform}</h3>
                <ul className="space-y-2">
                  {platform.services.map((service, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Check size={20} className="text-primary" weight="bold" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/checkout" data-testid="services-cta-button">
              <Button size="lg" className="px-8">
                Comenzar Ahora
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4">
              Lo que dicen nuestros clientes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'María G.',
                role: 'Influencer',
                comment: 'Excelente servicio, mis seguidores crecieron rápidamente y de forma natural.',
                rating: 5,
              },
              {
                name: 'Carlos R.',
                role: 'Emprendedor',
                comment: 'La mejor inversión para mi negocio. Resultados reales y entrega rápida.',
                rating: 5,
              },
              {
                name: 'Ana L.',
                role: 'Content Creator',
                comment: 'Plataforma fácil de usar y soporte excelente. Muy recomendado.',
                rating: 5,
              },
            ].map((testimonial, index) => (
              <Card
                key={index}
                className="p-6 border-border"
                data-testid={`testimonial-card-${index + 1}`}
              >
                <div className="flex items-center gap-1 mb-4 text-yellow-500">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed">&ldquo;{testimonial.comment}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-primary">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <div className="font-medium">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Warranty */}
      <section className="py-20 bg-muted/30" data-testid="warranty-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <WarrantySection selected={null} onSelect={() => {}} showHeader={true} />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-muted/30" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4">
              Preguntas frecuentes
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                question: '¿Es seguro usar este servicio?',
                answer:
                  'Sí, totalmente seguro. Usamos métodos aprobados que no violan los términos de servicio de las plataformas.',
              },
              {
                question: '¿Cuánto tiempo tarda la entrega?',
                answer:
                  'La mayoría de los pedidos comienzan a procesarse instantáneamente. El tiempo de entrega varía según el servicio y la cantidad.',
              },
              {
                question: '¿Hacen reembolsos?',
                answer:
                  'Solo procesamos reembolsos en un único caso: si el pedido no llega a entregarse. En cualquier otra situación (bajas, caídas, etc.) la garantía de recarga cubre el reemplazo sin costo dentro del período contratado.',
              },
              {
                question: '¿Cómo funciona la garantía de recarga?',
                answer:
                  'Todos los pedidos incluyen 3 días gratis de garantía. Podés extender la cobertura a 30 días ($2.000), 60 días ($5.100) o 1 año ($14.500) en el checkout. Si tu pedido sufre alguna baja en el período contratado, lo recargamos sin costo.',
              },
              {
                question: '¿Necesito proporcionar mi contraseña?',
                answer:
                  'No, nunca solicitamos contraseñas. Solo necesitamos el enlace público de tu perfil o publicación.',
              },
              {
                question: '¿Puedo elegir una cantidad personalizada?',
                answer:
                  'Sí. En el checkout encontrás la opción "Personalizado" donde podés ingresar la cantidad exacta que necesites (mínimo 50 unidades). El precio se calcula automáticamente.',
              },
            ].map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} data-testid={`faq-item-${index + 1}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden ring-1 ring-[#00E676]/40">
                  <img
                    src="https://customer-assets.emergentagent.com/job_social-boost-353/artifacts/evt7j1yg_%2B54%209%203447%2043-8147%2020260621_160302.jpg"
                    alt="Agency Market"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="font-bold text-lg">Agency Market</div>
                  <div className="text-xs text-[#00E676] font-semibold tracking-widest">.AR</div>
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-6 max-w-md">
                Tu socio confiable para el crecimiento en redes sociales. Seguidores, likes y vistas
                de calidad para Instagram, TikTok y Facebook.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/agencymarket.arg?igsh=cmJ2Y3V1a25qbDls"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="group relative w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500 via-purple-500 to-orange-400 flex items-center justify-center transition-transform hover:-translate-y-0.5"
                  aria-label="Seguinos en Instagram"
                >
                  <InstagramLogo size={22} weight="fill" className="text-white" />
                </a>
                <a
                  href="https://www.facebook.com/share/1EL8WAAewC/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook-link"
                  className="group relative w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center transition-transform hover:-translate-y-0.5"
                  aria-label="Seguinos en Facebook"
                >
                  <FacebookLogo size={22} weight="fill" className="text-white" />
                </a>
                <a
                  href="https://wa.me/541171740757"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="group relative w-11 h-11 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center transition-transform hover:-translate-y-0.5"
                  aria-label="Contactanos por WhatsApp"
                >
                  <WhatsappLogo size={22} weight="fill" className="text-white" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Instagram</li>
                <li>TikTok</li>
                <li>Facebook</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Soporte</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/checkout" className="hover:text-primary transition-colors">
                    Hacer pedido
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-primary transition-colors">
                    Iniciar sesión
                  </Link>
                </li>
                <li>FAQ</li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-border text-center text-sm text-muted-foreground space-y-2">
            <div className="flex items-center justify-center gap-2" data-testid="live-status">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-75 animate-ping"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#00E676]"></span>
              </span>
              <span className="text-[#00E676] font-semibold">Sistema en línea</span>
              <span className="text-muted-foreground">·</span>
              <span>Entregas automáticas activas 24/7</span>
            </div>
            <p>&copy; 2026 Agency Market .AR — Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};